import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { Zap, Moon, Sun, ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NavigationMenu />
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg">FlipValue</span>
            </button>
          </div>
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 py-8 w-full">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
        <p className="text-muted-foreground mb-6">Last updated: January 12, 2026</p>

        <div className="prose prose-sm dark:prose-invert max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Information We Collect</h2>
            <p className="text-muted-foreground mb-2">We collect information you provide directly to us, such as when you:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Create an account (email, name, password) or sign in via Google OAuth</li>
              <li>Use our search and valuation features</li>
              <li>Upload images for AI-powered item identification</li>
              <li>Add items to your inventory</li>
              <li>Set price alerts and release reminders</li>
              <li>Select your preferred eBay marketplace region</li>
              <li>Subscribe to our services or make purchases</li>
              <li>Participate in our referral program</li>
              <li>Contact us for support</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. How We Use Your Information</h2>
            <p className="text-muted-foreground mb-2">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Provide personalized market data from your selected regional eBay marketplace</li>
              <li>Send price alerts, release reminders, and notification emails based on your preferences</li>
              <li>Process images through AI services to identify items (images are not stored after processing)</li>
              <li>Send technical notices, updates, and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Track and credit referral rewards</li>
              <li>Analyze usage patterns to improve user experience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. Third-Party Services</h2>
            <p className="text-muted-foreground mb-2">We use trusted third-party services to provide our features:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Stripe:</strong> Payment processing (we do not store your full payment card details)</li>
              <li><strong>Supabase:</strong> Authentication and data storage</li>
              <li><strong>Google OAuth:</strong> Optional social login (we receive only your email and name)</li>
              <li><strong>eBay API:</strong> Market pricing data from your selected region</li>
              <li><strong>OpenAI:</strong> AI-powered item identification (images are processed but not retained)</li>
              <li><strong>Resend:</strong> Transactional email delivery</li>
            </ul>
            <p className="text-muted-foreground mt-2">Each third-party service has its own privacy policy governing the use of your information.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Information Sharing</h2>
            <p className="text-muted-foreground">We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mt-2">
              <li>With your consent</li>
              <li>To comply with legal obligations</li>
              <li>To protect our rights and prevent fraud</li>
              <li>With service providers who assist in our operations (e.g., payment processing, email delivery)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Regional Preferences</h2>
            <p className="text-muted-foreground">We store your selected eBay marketplace region to provide localized pricing data. This preference is used solely to fetch relevant market data from your preferred region (e.g., US, UK, Germany, France, Australia, Canada, Italy, Spain) and can be changed at any time in your settings.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Notification Preferences</h2>
            <p className="text-muted-foreground">We store your notification preferences to send you relevant alerts including price alerts for inventory items, release reminders, and optional weekly digests. You can manage these preferences in your account settings at any time.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Data Security</h2>
            <p className="text-muted-foreground">We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption of sensitive data, secure server infrastructure, and secure authentication methods.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Data Retention</h2>
            <p className="text-muted-foreground">We retain your personal information for as long as your account is active or as needed to provide you services. Images uploaded for item identification are processed in real-time and are not permanently stored. You may request deletion of your account and associated data at any time by contacting us.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Cookies and Local Storage</h2>
            <p className="text-muted-foreground">We use cookies, local storage, and similar technologies to enhance your experience, maintain your session, store preferences (like theme and region), and analyze usage. You can control these through your browser settings, though some features may not work properly if disabled.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Your Rights</h2>
            <p className="text-muted-foreground mb-2">You have the right to:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing of your data</li>
              <li>Export your data in a portable format</li>
              <li>Opt out of marketing communications</li>
              <li>Manage your notification preferences</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Children's Privacy</h2>
            <p className="text-muted-foreground">Our services are not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. International Data</h2>
            <p className="text-muted-foreground">We support users from multiple regions and may transfer your data internationally to provide our services. By using FlipValue, you consent to the transfer of your information to countries that may have different data protection laws than your country of residence.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">13. Changes to This Policy</h2>
            <p className="text-muted-foreground">We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">14. Contact Us</h2>
            <p className="text-muted-foreground">If you have any questions about this Privacy Policy, please contact us at:</p>
            <p className="text-primary mt-2">support@flipvalue.app</p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
